import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';

interface Event {
  id: string;
  title: string;
  description: string | null;
  event_type: 'meal' | 'program' | 'service' | 'other';
  day_of_week: number;
  time: string;
  location: string;
}

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function EventsScreen() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('events')
        .select('*')
        .order('day_of_week', { ascending: true })
        .order('time', { ascending: true });

      if (fetchError) throw fetchError;
      setEvents(data || []);
    } catch (err) {
      setError('Failed to load events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const eventsByDay = dayNames.map((_, index) => ({
    day: index,
    name: dayNames[index],
    events: events.filter((e) => e.day_of_week === index),
  }));

  const daysWithEvents = eventsByDay.filter((d) => d.events.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 border-4 border-[#B85C38] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#B85C38] py-12 px-6 text-center">
        <h1 className="text-4xl font-bold text-white mb-3">Our Programs & Events</h1>
        <p className="text-lg text-white">Weekly activities designed to support community and recovery</p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-block text-gray-600 hover:text-gray-900 mb-6">
          ← Back to Home
        </Link>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {daysWithEvents.map((dayInfo) => (
            <button
              key={dayInfo.day}
              onClick={() => setSelectedDay(selectedDay === dayInfo.day ? null : dayInfo.day)}
              className={`p-6 rounded-xl shadow-md transition-all ${
                selectedDay === dayInfo.day
                  ? 'bg-[#B85C38] text-white'
                  : 'bg-white text-gray-900 hover:shadow-lg'
              }`}
            >
              <h3 className={`text-2xl font-bold mb-1 ${selectedDay === dayInfo.day ? 'text-white' : 'text-gray-900'}`}>
                {dayInfo.name}
              </h3>
              <p className={`text-sm ${selectedDay === dayInfo.day ? 'text-[#E8DDD0]' : 'text-gray-600'}`}>
                {dayInfo.events.length} event{dayInfo.events.length !== 1 ? 's' : ''}
              </p>
            </button>
          ))}
        </div>

        {selectedDay !== null && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{dayNames[selectedDay]} Events</h2>
            <div className="space-y-4">
              {eventsByDay[selectedDay].events.map((event) => (
                <div key={event.id} className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#B85C38]">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{event.title}</h3>
                  <p className="text-sm text-gray-500 mb-3 capitalize">{event.event_type}</p>
                  {event.description && (
                    <p className="text-gray-700 leading-relaxed mb-4">{event.description}</p>
                  )}
                  <div className="space-y-2">
                    <p className="text-gray-700 font-semibold">Time: {event.time.substring(0, 5)}</p>
                    <p className="text-gray-700 font-semibold">Location: {event.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedDay === null && (
          <div className="space-y-4 mb-8">
            {daysWithEvents.map((dayInfo) =>
              dayInfo.events.map((event) => (
                <div key={event.id} className="bg-white rounded-xl p-6 shadow-md border-l-4 border-[#B85C38]">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{event.title}</h3>
                  <p className="text-sm text-gray-500 mb-3">{dayInfo.name}</p>
                  {event.description && (
                    <p className="text-gray-700 leading-relaxed mb-4">{event.description}</p>
                  )}
                  <div className="space-y-2">
                    <p className="text-gray-700 font-semibold">Time: {event.time.substring(0, 5)}</p>
                    <p className="text-gray-700 font-semibold">Location: {event.location}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="bg-[#E8DDD0] rounded-xl p-8 border border-[#B85C38]">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">Join Us This Week</h3>
          <p className="text-gray-700 leading-relaxed mb-4 text-center">
            Whether you're looking for community connection, recovery support, spiritual growth, or a meal,
            Harvest House Tobique has programs throughout the week designed to meet you where you are.
          </p>
          <p className="text-gray-600 text-sm text-center">
            All programs are free and open to the community. No appointment needed.
          </p>
        </div>
      </div>
    </div>
  );
}
